import { useEffect } from 'react';

const OS_QUERY = '(prefers-reduced-motion: reduce)';

/** Lives on `<html>` so plain CSS can gate on the in-game toggle the same way
 *  it gates on the OS media query (the `:root[data-reduced-motion]` mirror
 *  blocks next to each `@media (prefers-reduced-motion)` block). */
export const REDUCED_MOTION_ATTRIBUTE = 'data-reduced-motion';

/**
 * Single read point for "should motion be reduced right now": the OS-level
 * media query OR the in-game Settings toggle (mirrored onto
 * `<html data-reduced-motion>` before first render in main.tsx and kept in
 * sync by useReducedMotionAttribute). Animation code must call this rather
 * than matchMedia directly — matchMedia alone misses the toggle.
 */
export function prefersReducedMotion(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.hasAttribute(REDUCED_MOTION_ATTRIBUTE)) return true;
  return typeof window.matchMedia === 'function' && window.matchMedia(OS_QUERY).matches;
}

export function syncReducedMotionAttribute(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.toggleAttribute(REDUCED_MOTION_ATTRIBUTE, enabled);
}

/** Keeps the root attribute tracking the preference for the life of the app
 *  shell. Deliberately no cleanup: the shell never unmounts, and dropping the
 *  attribute mid-session would restart suppressed animations. */
export function useReducedMotionAttribute(enabled: boolean): void {
  useEffect(() => {
    syncReducedMotionAttribute(enabled);
  }, [enabled]);
}
